import React, { useContext, useEffect, useState } from "react";
import Moment from "moment";

import { CustomersContext } from "../../context/customersContext";
import { SalesContext } from "../../context/salesContext";
import { fetchSalesList } from "../../api/salesApi";
import NoteModal from "../modals/NoteModal";
import SaleModal from "../modals/SaleModal";
import CustomerSalesTable from "../tables/CustomerSalesTable";
import { Badge, Button, Input, Select } from "../ui";

const FIELD_LABELS = {
    company: "Company",
    firstName: "First Name",
    lastName: "Last Name",
    mobile: "Mobile",
    email: "Email",
    address: "Address",
    city: "City",
    province: "Province",
    postalCode: "Postal Code",
};

const buildDraft = (c) => ({
    contractor: c.contractor || "",
    company: c.company || "",
    firstName: c.firstName || "",
    lastName: c.lastName || "",
    mobile: c.mobile || "",
    email: c.email || "",
    address: c.address || "",
    city: c.city || "",
    province: c.province || "",
    postalCode: c.postalCode || "",
    notes: c.notes || [],
    recentActivities: c.recentActivities || [],
    updatedDate: c.updatedDate,
});

const collectActivityChanges = (prev, curr) =>
    Object.keys(FIELD_LABELS)
        .concat("contractor")
        .filter((key) => (prev[key] || "") !== (curr[key] || ""))
        .map(
            (key) =>
                `${Moment().format("LLL")}: ${key} was changed from '${
                    prev[key] || ""
                }' to '${curr[key] || ""}'`,
        );

const Customer = ({ customer, handleClickOpen }) => {
    const { action } = useContext(CustomersContext);
    const salesAction = useContext(SalesContext).action;

    const [draft, setDraft] = useState(() => buildDraft(customer));
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState("notes");
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [customerSales, setCustomerSales] = useState([]);
    const [openSale, setOpenSale] = useState(false);
    const [selectedSale, setSelectedSale] = useState();

    useEffect(() => {
        setDraft(buildDraft(customer));
        setIsEditing(false);
    }, [customer]);

    useEffect(() => {
        if (!customer || !customer._id) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchSalesList({
                    page: 1,
                    limit: 200,
                    customerId: customer._id,
                });
                if (cancelled || !data) return;
                setCustomerSales(data.salesList || []);
            } catch (error) {
                console.log("Loading customer sales failed: ", error);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer && customer._id]);

    const handleField = (event) => {
        const { name, value } = event.target;
        setDraft((d) => ({ ...d, [name]: value }));
    };

    const handleCancel = () => {
        setDraft(buildDraft(customer));
        setIsEditing(false);
    };

    const handleSave = async (event) => {
        event.preventDefault();
        const changes = collectActivityChanges(customer, draft);
        const payload = {
            ...draft,
            recentActivities: [...(draft.recentActivities || []), ...changes],
        };
        try {
            await action.updateCustomer(customer._id, payload);
            setIsEditing(false);
            handleClickOpen && handleClickOpen(customer._id);
        } catch (error) {
            console.log("Error: ", error);
        }
    };

    const persistList = (key, list) => {
        setDraft((d) => ({ ...d, [key]: list }));
        action.updateCustomer(customer._id, { ...draft, [key]: list });
    };

    const addNote = (note) => persistList("notes", [...(draft.notes || []), note]);

    const removeReversedAt = (key) => (selectedIndex) => {
        const next = (draft[key] || [])
            .slice(0)
            .reverse()
            .filter((_, idx) => idx !== selectedIndex)
            .slice(0)
            .reverse();
        persistList(key, next);
    };

    const openSaleModal = async (saleId) => {
        setOpenSale(true);
        const sale = await salesAction.getSale(saleId);
        setSelectedSale(sale);
    };

    const closeSaleModal = () => {
        setOpenSale(false);
        setSelectedSale();
    };

    const isContractor = (draft.contractor || "").toLowerCase() === "yes";
    const fullName =
        `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || "Unnamed customer";
    const metaParts = [customer.city].filter(Boolean);
    metaParts.push(`Updated ${Moment(draft.updatedDate).format("ll")}`);

    return (
        <div className="space-y-5">
            <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-semibold tracking-tight text-ink-900">
                        {fullName}
                    </h2>
                    <Badge tone={isContractor ? "brand" : "neutral"}>
                        {isContractor ? "Contractor" : "Customer"}
                    </Badge>
                </div>
                <p className="mt-0.5 text-sm text-ink-500">{metaParts.join(" · ")}</p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <section className="rounded-xl border border-ink-100 bg-white p-3 sm:p-5">
                    <div className="mb-4 flex items-center justify-between gap-2">
                        <h3 className="detail-section-title">Customer information</h3>
                        {isEditing ? (
                            <div className="flex shrink-0 gap-2">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="rounded-md px-2.5 py-1 text-xs font-medium text-ink-600 transition-colors hover:bg-ink-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-700"
                                >
                                    <i className="fas fa-check" aria-hidden="true" />
                                    Save
                                </button>
                            </div>
                        ) : (
                            <Button
                                variant="secondary"
                                size="sm"
                                className="detail-action-btn"
                                onClick={() => setIsEditing(true)}
                                leadingIcon={<i className="fas fa-pen" aria-hidden="true" />}
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                    {isEditing ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <Select
                                id="contractor"
                                name="contractor"
                                label="Contractor"
                                value={draft.contractor}
                                onChange={handleField}
                            >
                                <option value="No">No</option>
                                <option value="Yes">Yes</option>
                            </Select>
                            {Object.entries(FIELD_LABELS).map(([key, label]) => (
                                <Input
                                    key={key}
                                    id={key}
                                    name={key}
                                    label={label}
                                    type={key === "email" ? "email" : "text"}
                                    value={draft[key]}
                                    onChange={handleField}
                                />
                            ))}
                        </div>
                    ) : (
                        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                            <Field label="Contractor" value={isContractor ? "Yes" : "No"} />
                            {Object.entries(FIELD_LABELS).map(([key, label]) => (
                                <Field key={key} label={label} value={draft[key]} />
                            ))}
                        </dl>
                    )}
                </section>

                <section className="flex flex-col rounded-xl border border-ink-100 bg-white p-3 sm:p-5">
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex gap-1 rounded-lg bg-ink-50 p-1">
                            <TabButton
                                active={activeTab === "notes"}
                                onClick={() => setActiveTab("notes")}
                            >
                                Notes
                            </TabButton>
                            <TabButton
                                active={activeTab === "activity"}
                                onClick={() => setActiveTab("activity")}
                            >
                                Recent Activity
                            </TabButton>
                        </div>
                        {activeTab === "notes" && (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowNotesModal(true)}
                                leadingIcon={<i className="fas fa-plus" aria-hidden="true" />}
                            >
                                Add note
                            </Button>
                        )}
                    </div>

                    <div className="max-h-96 overflow-y-auto pr-1">
                        {activeTab === "notes" ? (
                            <NotesList
                                notes={draft.notes}
                                isEditing={isEditing}
                                onRemove={removeReversedAt("notes")}
                            />
                        ) : (
                            <ActivityList
                                activity={draft.recentActivities}
                                isEditing={isEditing}
                                onRemove={removeReversedAt("recentActivities")}
                            />
                        )}
                    </div>

                    <NoteModal
                        showNotesModal={showNotesModal}
                        setShowNotesModal={setShowNotesModal}
                        addNote={addNote}
                    />
                </section>
            </div>

            <section>
                <h3 className="detail-section-title mb-3">Purchase History</h3>
                <CustomerSalesTable customerSales={customerSales} openSaleModal={openSaleModal} />
                <SaleModal
                    selectedSale={selectedSale}
                    setSelectedSale={setSelectedSale}
                    open={openSale}
                    setOpen={setOpenSale}
                    handleClose={closeSaleModal}
                    openSaleModal={openSaleModal}
                />
            </section>
        </div>
    );
};

const Field = ({ label, value }) => (
    <div className="min-w-0">
        <dt className="detail-field-label">{label}</dt>
        <dd className="mt-0.5 break-words text-sm text-ink-900">{value || "—"}</dd>
    </div>
);

const TabButton = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            active ? "bg-brand-600 text-white shadow-sm" : "text-ink-500 hover:text-ink-700"
        }`}
    >
        {children}
    </button>
);

const ListItem = ({ children, onRemove }) => (
    <div className="group relative rounded-lg border border-ink-100 bg-ink-50 p-3 pr-9">
        {children}
        {onRemove && (
            <button
                type="button"
                onClick={onRemove}
                aria-label="Remove"
                className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded text-ink-400 hover:bg-ink-100 hover:text-ink-700"
            >
                <i className="fas fa-times text-xs" aria-hidden="true" />
            </button>
        )}
    </div>
);

const EmptyList = ({ children }) => (
    <div className="rounded-lg border border-dashed border-ink-200 bg-ink-50 px-4 py-8 text-center text-sm text-ink-500">
        {children}
    </div>
);

const NotesList = ({ notes, isEditing, onRemove }) => {
    if (!notes || notes.length === 0) return <EmptyList>No notes yet.</EmptyList>;
    return (
        <div className="space-y-2">
            {notes
                .slice(0)
                .reverse()
                .map((note, idx) => (
                    <ListItem key={idx} onRemove={isEditing ? () => onRemove(idx) : null}>
                        <p className="text-xs font-medium text-ink-500">
                            {Moment(note.date).format("LLL")}
                        </p>
                        <p className="mt-1 text-sm text-ink-800">{note.description}</p>
                    </ListItem>
                ))}
        </div>
    );
};

const ActivityList = ({ activity, isEditing, onRemove }) => {
    if (!activity || activity.length === 0) return <EmptyList>No activity yet.</EmptyList>;
    return (
        <div className="space-y-2">
            {activity
                .slice(0)
                .reverse()
                .map((entry, idx) => (
                    <ListItem key={idx} onRemove={isEditing ? () => onRemove(idx) : null}>
                        <p className="text-sm text-ink-800">{entry}</p>
                    </ListItem>
                ))}
        </div>
    );
};

export default Customer;
